import React, { useState, useRef, useEffect } from "react";
import { exportToPDF, exportToExcel } from "./ExportUtils";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { AlertTriangle, Download, FileText, FileSpreadsheet } from "lucide-react";
import { Button } from "../../../components/ui/button";

export default function ExportButton({ selectedRows, produits }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const menuRef = useRef(null);

  const handleExport = (format) => {
    if (selectedRows.length === 0) {
      setMenuOpen(false);
      setErrorDialogOpen(true);
      return;
    }

    const dataToExport = selectedRows.map((id) =>
      produits.find((prod) => prod.id === id)
    );

    if (!dataToExport.length) {
      setMenuOpen(false);
      setErrorDialogOpen(true);
      return;
    }

    if (format === "pdf") {
      exportToPDF(dataToExport);
    } else if (format === "excel") {
      exportToExcel(dataToExport);
    }

    setMenuOpen(false);
  };

  // Fermer le menu si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      {/* Bouton principal */}
      <div className="relative inline-block text-left" ref={menuRef}>
        <Button
          onClick={() => setMenuOpen((prev) => !prev)}
          className="bg-[var(--hp-success-600)] hover:bg-[var(--hp-success-700)] text-white gap-2 shadow-hp-sm"
        >
          <Download className="h-4 w-4" />
          Exporter
        </Button>

        {/* Dropdown Menu */}
        {menuOpen && (
          <div className="absolute right-0 mt-2 w-52 origin-top-right rounded-md bg-surface shadow-lg ring-1 ring-black ring-opacity-5 z-20 border border-workspace">
            <div className="py-1">
              <button
                type="button"
                onClick={() => handleExport("pdf")}
                className="flex w-full items-center px-4 py-2 text-sm text-muted hover:bg-surface-muted transition-colors"
              >
                <FileText className="h-4 w-4 text-[var(--hp-danger-500)] mr-2" />
                Exporter en PDF
              </button>
              <button
                type="button"
                onClick={() => handleExport("excel")}
                className="flex w-full items-center px-4 py-2 text-sm text-subtle hover:bg-surface-muted transition-colors"
              >
                <FileSpreadsheet className="h-4 w-4 text-[var(--hp-success-600)] mr-2" />
                Exporter en Excel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal d'erreur */}
      <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-ink-500/75 backdrop-blur-sm transition-opacity" />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <DialogPanel className="bg-surface rounded-lg shadow-xl transform transition-all w-full max-w-md border border-workspace">
            <div className="p-6">
              <div className="flex items-start">
                <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--hp-danger-50)] sm:mx-0 sm:h-10 sm:w-10">
                  <AlertTriangle className="h-6 w-6 text-[var(--hp-danger-600)]" aria-hidden="true" />
                </div>
                <div className="ml-4">
                  <DialogTitle as="h3" className="text-lg font-semibold text-primary">
                    Erreur d'exportation
                  </DialogTitle>
                  <div className="mt-2">
                    <p className="text-sm text-subtle">
                      Veuillez sélectionner au moins une ligne avant d'exporter les données.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-workspace px-4 py-3 sm:flex sm:flex-row-reverse">
              <Button
                variant="destructive"
                onClick={() => setErrorDialogOpen(false)}
                className="w-full sm:w-auto sm:ml-3"
              >
                OK
              </Button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
}
